import {
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  PageWrapper,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import { ReportType } from 'domain/models';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  Col,
  Form,
  Label,
  Row,
  Spinner,
} from 'reactstrap';

import { useReportRadinos } from './hooks/useRadinos';

export const RadinosPage = () => {
  const profileOptions = [
    { value: PROFILE_TYPE.ADMINISTRATOR, label: 'Administrador' },
    { value: PROFILE_TYPE.ASSISTANT, label: 'Assistente' },
    { value: PROFILE_TYPE.SUPPORT, label: 'Suporte' },
    { value: PROFILE_TYPE.STUDENT, label: 'Estudante' },
    { value: PROFILE_TYPE.TEACHER, label: 'Professor' },
    { value: PROFILE_TYPE.PRECEPTOR, label: 'Preceptor' },
    { value: PROFILE_TYPE.DIRECTOR, label: 'Diretor' },
    { value: PROFILE_TYPE.COORDINATOR, label: 'Coordenador' },
    { value: PROFILE_TYPE.ADMINISTRATIVE, label: 'Auxiliar' },
    {},
  ];

  const {
    validation,
    semestersList,
    requestsStatus,
    courseListOptions,
    organizationOptions,
    submitReport,
    requestGetAllCourses,
    requestGetAllSemesters,
    requestGetAllOrganizations,
  } = useReportRadinos();

  useEffect(() => {
    requestGetAllOrganizations();
  }, []);

  return (
    <>
      <PageWrapper title="Relatórios Radinos">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold">Relatórios Radinos</h4>
            <p className="text-muted mb-0">Relatórios de Radinos.</p>
          </div>
        </div>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Card>
            <CardHeader className="p-3">
              <Row>
                <Col md={4} xs={12}>
                  <Label className="form-label">Organização</Label>
                  <Select
                    isMulti
                    isClearable
                    isLoading={
                      requestsStatus.organizationLoading === STATUSPAGE.LOADING
                    }
                    isDisabled={
                      requestsStatus.organizationLoading === STATUSPAGE.LOADING
                    }
                    id="organizationHashIds"
                    name="organizationHashIds"
                    placeholder="Todas as Organizações"
                    value={validation.values.organizationHashIds}
                    onChange={(e: any) => {
                      validation.handleChange(
                        convertEventSelectToTarget('organizationHashIds', e),
                      );

                      if (e.length === 0) {
                        validation.setFieldValue('courseHashIds', []);
                      }

                      requestGetAllCourses({
                        organizationId: e.map((item) => item.value),
                      });
                    }}
                    onBlur={validation.handleBlur}
                    options={organizationOptions}
                  />
                </Col>

                <Col md={4} xs={12} className="mt-3 mt-md-0">
                  <Label className="form-label">Curso</Label>
                  <Select
                    options={courseListOptions}
                    name="courseHashIds"
                    id="courseHashIds"
                    isMulti
                    isLoading={
                      requestsStatus.coursesUser === STATUSPAGE.LOADING
                    }
                    isDisabled={
                      requestsStatus.coursesUser === STATUSPAGE.LOADING ||
                      validation.values.organizationHashIds.length === 0
                    }
                    value={validation.values.courseHashIds}
                    placeholder={'Todos os Cursos'}
                    isClearable={courseListOptions.length > 0}
                    onChange={(course: any) => {
                      validation.setFieldValue('courseHashIds', course);
                      validation.setFieldValue('semesterIds', []);
                      if (course.length === 0) {
                        validation.setFieldValue('semesterIds', []);
                        return;
                      }

                      requestGetAllSemesters({
                        organizationId:
                          validation.values.organizationHashIds.map(
                            (item) => item.value,
                          ),
                        courseId: course.map((item) => item.value),
                      });
                    }}
                  />
                </Col>

                <Col lg={4} className="mb-3">
                  <Label htmlFor="semesterIds" className="form-label">
                    Semestre
                  </Label>
                  <Select
                    isClearable
                    isMulti
                    id="semesterIds"
                    name="semesterIds"
                    placeholder="Todos os Semestres"
                    isDisabled={
                      requestsStatus.semestersLoading === STATUSPAGE.LOADING ||
                      validation.values.courseHashIds.length === 0
                    }
                    value={validation.values.semesterIds}
                    isLoading={
                      requestsStatus.semestersLoading === STATUSPAGE.LOADING
                    }
                    onChange={(e) => {
                      validation.handleChange(
                        convertEventSelectToTarget('semesterIds', e),
                      );
                    }}
                    onBlur={validation.handleBlur}
                    options={formatDataToLabelValue(
                      semestersList as never,
                      'id',
                      'nome',
                    )}
                  />
                </Col>

                <Col md={4}>
                  <Label className="form-label" htmlFor="roleIds">
                    Perfil
                  </Label>
                  <Select
                    isClearable
                    id="roleIds"
                    isMulti
                    name="roleIds"
                    placeholder="Selecione um perfil"
                    onBlur={validation.handleBlur}
                    value={validation.values.roleIds}
                    options={formatDataToLabelValue(
                      profileOptions as never,
                      'value',
                      'label',
                    )}
                    onChange={(e) => {
                      validation.handleChange(
                        convertEventSelectToTarget('roleIds', e),
                      );
                    }}
                  />
                </Col>

                <Col sm={12} md={6} lg={4} className="mb-3 mt-1">
                  <Row>
                    <Label htmlFor="initial-date" className="form-label">
                      Período
                    </Label>
                    <CustomDateRangePicker
                      initialStartDate={new Date(validation.values.period[0])}
                      initialEndDate={new Date(validation.values.period[1])}
                      cleanable={false}
                      placement="auto"
                      value={validation.values.period as any}
                      menuClassName="custom-datepicker-menu"
                      onChange={(selectedDates) => {
                        validation.handleChange({
                          target: {
                            id: 'period',
                            name: 'period',
                            value: selectedDates,
                          },
                        });
                      }}
                    />
                  </Row>
                </Col>
              </Row>
            </CardHeader>

            <div className="my-3 p-0 me-3 justify-content-end d-flex mt-3">
              <Button
                disabled={
                  requestsStatus.reportRadinoPdf === STATUSPAGE.LOADING ||
                  requestsStatus.reportRadinoExcel === STATUSPAGE.LOADING
                }
                className="me-2"
                color="primary"
                onClick={() => submitReport(ReportType.pdf)}
              >
                Exportar PDF <i className="ri-file-pdf-2-line"></i>
                {requestsStatus.reportRadinoPdf === STATUSPAGE.LOADING ? (
                  <Spinner size="sm" className="ms-2" />
                ) : null}
              </Button>

              <Button
                disabled={
                  requestsStatus.reportRadinoPdf === STATUSPAGE.LOADING ||
                  requestsStatus.reportRadinoExcel === STATUSPAGE.LOADING
                }
                color="primary"
                onClick={() => submitReport(ReportType.excel)}
              >
                Exportar Excel
                <i className="ms-1 ri-file-excel-2-line"></i>
                {requestsStatus.reportRadinoExcel === STATUSPAGE.LOADING ? (
                  <Spinner size="sm" className="ms-2" />
                ) : null}
              </Button>
            </div>
          </Card>
        </Form>
      </PageWrapper>
    </>
  );
};
